import {
  authPages,
  AUTH_ACTION_PAGE,
  EMAIL_VERIFICATION_PAGE,
  LOGOUT_PAGE,
  LOGIN_PAGE,
  SIGNUP_PAGE,
  OPTION_FLOW_PAGE,
} from 'constants/pages';
import { getSafeNextPath } from 'utils/client/safeNextPath';

const STORAGE_PREFIX = 'if:verification:';
export const VERIFICATION_SEND_COOLDOWN_SECONDS = 60;

const read = (key: string) => {
  try {
    return window.localStorage.getItem(STORAGE_PREFIX + key);
  } catch {
    return null;
  }
};
const write = (key: string, value: string | null) => {
  try {
    if (value === null) window.localStorage.removeItem(STORAGE_PREFIX + key);
    else window.localStorage.setItem(STORAGE_PREFIX + key, value);
  } catch {
    /* Storage is a convenience; Firebase remains authoritative. */
  }
};
export const getVerificationReturnPath = (next: unknown) => {
  const safe = getSafeNextPath(next, OPTION_FLOW_PAGE);
  const url = new URL(safe, 'https://www.insiderfinance.io');
  const pathname = url.pathname.replace(/\/+$/, '') || '/';
  if (
    url.origin !== 'https://www.insiderfinance.io' ||
    authPages.includes(pathname) ||
    [EMAIL_VERIFICATION_PAGE, LOGOUT_PAGE, AUTH_ACTION_PAGE].includes(pathname) ||
    [LOGIN_PAGE, SIGNUP_PAGE].some((path) => pathname === path || pathname.startsWith(path + '/'))
  )
    return OPTION_FLOW_PAGE;
  return `${url.pathname}${url.search}${url.hash}`;
};
export const getVerificationHref = (next: unknown) =>
  `${EMAIL_VERIFICATION_PAGE}?next=${encodeURIComponent(getVerificationReturnPath(next))}`;
export const getVerificationSendDeadline = (uid: string, email: string) =>
  Number(read(`sent:${uid}:${email.toLowerCase()}`)) || 0;
export const rememberVerificationSend = (uid: string, email: string, deadline: number) =>
  write(`sent:${uid}:${email.toLowerCase()}`, String(deadline));
export const getVerificationErrorCode = (error: unknown): string => {
  const code = (error as { code?: unknown })?.code;
  return typeof code === 'string' ? code : '';
};
export const isVerificationSessionExpired = (error: unknown) =>
  [
    'auth/user-token-expired',
    'auth/invalid-user-token',
    'auth/user-disabled',
    'auth/user-not-found',
  ].includes(getVerificationErrorCode(error)) ||
  (error as { statusCode?: number })?.statusCode === 401;
export const isTransientVerificationError = (error: unknown) => {
  if (isVerificationSessionExpired(error)) return false;
  const code = getVerificationErrorCode(error);
  if ((error as { name?: string })?.name === 'AbortError' || code.startsWith('verification/'))
    return false;
  if (code.startsWith('auth/'))
    return ['auth/network-request-failed', 'auth/internal-error', 'auth/timeout'].includes(code);
  const status = (error as { statusCode?: number })?.statusCode;
  return !status || status === 408 || status >= 500;
};

/** Quiet, bounded recovery. Never repeat a popup or a user mistake. */
export const retryVerificationTask = async <T>(
  task: () => Promise<T>,
  {
    signal,
    attempts = 3,
    timeoutMs = 12_000,
  }: { signal?: AbortSignal; attempts?: number; timeoutMs?: number } = {},
): Promise<T> => {
  const abortError = () =>
    Object.assign(new Error('Verification canceled'), { name: 'AbortError' });
  for (let attempt = 0; attempt < attempts; attempt += 1) {
    if (signal?.aborted) throw abortError();
    let timer: ReturnType<typeof setTimeout>;
    let onAbort: () => void = () => undefined;
    try {
      const stop = new Promise<never>((_, reject) => {
        timer = setTimeout(
          () =>
            reject(
              Object.assign(new Error('Verification timeout'), {
                code: 'verification/task-timeout',
              }),
            ),
          timeoutMs,
        );
        onAbort = () => reject(abortError());
        signal?.addEventListener('abort', onAbort, { once: true });
      });
      return await Promise.race([task(), stop]);
    } catch (error) {
      if (signal?.aborted || !isTransientVerificationError(error) || attempt === attempts - 1)
        throw error;
    } finally {
      clearTimeout(timer!);
      signal?.removeEventListener('abort', onAbort);
    }
    await new Promise<void>((resolve, reject) => {
      const onStop = () => {
        clearTimeout(delay);
        reject(abortError());
      };
      const delay = setTimeout(
        () => {
          signal?.removeEventListener('abort', onStop);
          resolve();
        },
        attempt === 0 ? 1000 : 3000,
      );
      signal?.addEventListener('abort', onStop, { once: true });
      if (signal?.aborted) onStop();
    });
  }
  throw new Error('Verification retry exhausted');
};
